import isEqual from "lodash/isEqual"
import set from "lodash/set"
import get from "lodash/get"
import Stack from "@mui/material/Stack"
import Chip from "@mui/material/Chip"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"

interface OptionProps {
  label: string
  field: string
  value?: any
  search: object
  onSearch: Function
  multiple?: boolean
}

interface OptionsFilterProps<T> {
  title: string
  collection: Array<T>
  optionLabel: (item: T) => string
  optionValue: (item: T) => any
  field: string
  search: object
  onSearch: Function
  multiple?: boolean
}

const FilterWrapper = ({ children, title }) => (
  <Grid container alignItems="center" my={0.5}>
    <Grid item mr={2} width={140}>
      <Typography variant="subtitle1">{title}</Typography>
    </Grid>
    <Grid item>
      <Stack direction="row" spacing={1}>
        {children}
      </Stack>
    </Grid>
  </Grid>
)

export const OptionsFilter = <T extends any>({
  title,
  collection,
  optionLabel,
  optionValue,
  ...optionProps
}: OptionsFilterProps<T>) => (
  <FilterWrapper title={title}>
    <Stack direction="row" spacing={1} flexWrap="wrap">
      <Option label="All" value={undefined} {...optionProps} />
      {collection.map((item) => (
        <Option
          label={optionLabel(item)}
          value={optionValue(item)}
          key={optionLabel(item)}
          {...optionProps}
        />
      ))}
    </Stack>
  </FilterWrapper>
)

const Option = ({ field, label, value, search, onSearch, multiple = false }: OptionProps) => {
  const searchedFieldState = get(search, field)
  const isActive = isOptionActive(searchedFieldState, value, multiple)
  const searchedFields = field.split(".")
  const firstSearchedField = searchedFields[0] as string
  const searchParams = resolveNewSearchParams(searchedFieldState, value, multiple, isActive)

  const searched = set({}, field, searchParams)

  const onClick = () =>
    onSearch((prevState) => {
      if (searchedFields.length > 1) {
        return {
          ...prevState,
          [firstSearchedField]: {
            ...prevState[firstSearchedField],
            ...searched[firstSearchedField],
          },
        }
      } else {
        return { ...prevState, ...searched }
      }
    })

  return (
    <Chip
      color="primary"
      label={label}
      onClick={onClick}
      variant={isActive ? "filled" : "outlined"}
    />
  )
}

function isOptionActive(
  searchedFieldValue: { in: any[] } | any | undefined,
  value: any,
  multiple: boolean
) {
  if (multiple && searchedFieldValue) {
    const searchedValues = searchedFieldValue["in"]
    if (!searchedValues) return false

    for (let searchedValue of searchedValues) {
      if (isEqual(searchedValue, value)) {
        return true
      }
    }
  } else {
    return isEqual(searchedFieldValue, value)
  }
  return false
}

function resolveNewSearchParams(searchedFieldState, newValue, multiple, isActive) {
  const searchValue = isActive ? undefined : newValue
  if (!multiple) {
    return searchValue
  }

  // If value is undefined, then All/empty option has been selected.
  // In that case don't need any search params for this field.
  if (newValue === undefined) {
    return undefined
  }

  let currentValues = searchedFieldState ? searchedFieldState["in"] : []
  let newValues = currentValues.filter((value) => value !== newValue) // if new value is in current values, that means option is toggled off

  if (searchValue) {
    newValues = newValues.concat([searchValue]) // if new value is not yet active, add it to search params
  }

  if (newValues.length === 0) {
    return undefined
  }

  return { in: newValues }
}
